if (!customElements.get("collections-filtering")) {
  class CollectionsFiltering extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector("form");
      this.button = this.querySelector('button[type="submit"]');
      this.collectionSelect = this.querySelector("select.dropdown-collection");
      this.collectionInput = this.querySelector("input.input-collection");
      this.selects = Array.from(this.querySelectorAll("select.dropdown-filter"));

      this.fitmentData = null;

      this.fieldMap = {
        year: this.findSelect("Year"),
        make: this.findSelect("Make"),
        model: this.findSelect("Model"),
        subModel: this.findSelect("Sub Model"),
        engine: this.findSelect("Engine"),
      };

      this.setupEventListeners();
      this.loadFitmentData();
    }

    findSelect(label) {
      return this.selects.find((select) => {
        return this.normalize(select.dataset.label) === this.normalize(label);
      });
    }

    normalize(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[-_]/g, " ")
        .replace(/\s+/g, " ");
    }

    setupEventListeners() {
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();

        const url = this.constructFitmentURL();

        if (url) {
          window.location = url;
        }
      });

      this.collectionSelect?.addEventListener("change", () => {
        this.resetAllFilters();
        this.populateYears();
      });

      this.fieldMap.year?.addEventListener("change", () => this.onYearChange());
      this.fieldMap.make?.addEventListener("change", () => this.onMakeChange());
      this.fieldMap.model?.addEventListener("change", () => this.onModelChange());
      this.fieldMap.subModel?.addEventListener("change", () => this.onSubModelChange());
      this.fieldMap.engine?.addEventListener("change", () => this.onEngineChange());
    }

    async loadFitmentData() {
      const fitmentUrl = this.dataset.fitmentUrl;

      if (!fitmentUrl) {
        console.error("M7 Fitment JSON URL not found.");
        return;
      }

      try {
        this.toggleLoading(true);

        const response = await fetch(fitmentUrl);

        if (!response.ok) {
          throw new Error(`Failed to load fitment data: ${response.status}`);
        }

        this.fitmentData = await response.json();

        this.resetAllFilters();
        this.populateYears();
      } catch (error) {
        console.error(error);
      } finally {
        this.toggleLoading(false);
      }
    }

    getCollectionUrl() {
      return (
        this.collectionSelect?.value ||
        this.collectionInput?.value ||
        window.routes?.all_products_url ||
        "/collections/all"
      );
    }

    resetAllFilters() {
      this.selects.forEach((select) => {
        this.clearSelect(select);
        this.disableSelect(select);
      });

      this.button.disabled = true;
    }

    clearSelect(select) {
      if (!select) return;

      const options = select.querySelectorAll("option:not(.default)");

      options.forEach((option) => {
        option.remove();
      });

      const defaultOption = select.querySelector("option.default");

      if (defaultOption) {
        defaultOption.selected = true;
      }
    }

    enableSelect(select) {
      if (!select) return;

      select.disabled = false;
      select.closest(".form-group")?.classList.remove("disabled");
    }

    disableSelect(select) {
      if (!select) return;

      select.disabled = true;
      select.closest(".form-group")?.classList.add("disabled");
    }

    populateSelect(select, values) {
      if (!select) return;

      this.clearSelect(select);

      const cleanValues = Array.from(
        new Set(
          values
            .map((value) => String(value || "").trim())
            .filter((value) => value !== "")
        )
      ).sort((a, b) => {
        return String(a).localeCompare(String(b), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });

      cleanValues.forEach((value) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = value;
        select.appendChild(option);
      });

      if (cleanValues.length > 0) {
        this.enableSelect(select);
      } else {
        this.disableSelect(select);
      }
    }

    populateYears() {
      if (!this.fitmentData || !this.fieldMap.year) return;

      const years = Object.keys(this.fitmentData);
      this.populateSelect(this.fieldMap.year, years);
    }

    onYearChange() {
      const year = this.fieldMap.year?.value;

      this.clearAndDisableAfter("year");
      this.button.disabled = true;

      if (!year || !this.fitmentData?.[year]) return;

      const makes = Object.keys(this.fitmentData[year]);
      this.populateSelect(this.fieldMap.make, makes);
    }

    onMakeChange() {
      const year = this.fieldMap.year?.value;
      const make = this.fieldMap.make?.value;

      this.clearAndDisableAfter("make");
      this.button.disabled = true;

      if (!year || !make || !this.fitmentData?.[year]?.[make]) return;

      const models = Object.keys(this.fitmentData[year][make]);
      this.populateSelect(this.fieldMap.model, models);
    }

    onModelChange() {
      const year = this.fieldMap.year?.value;
      const make = this.fieldMap.make?.value;
      const model = this.fieldMap.model?.value;

      this.clearAndDisableAfter("model");
      this.button.disabled = true;

      if (!year || !make || !model || !this.fitmentData?.[year]?.[make]?.[model]) {
        return;
      }

      const subModelsRaw = Object.keys(this.fitmentData[year][make][model]);

      const subModels = subModelsRaw
        .map((subModel) => String(subModel || "").trim())
        .filter((subModel) => subModel !== "");

      if (subModels.length > 0) {
        this.populateSelect(this.fieldMap.subModel, subModels);
        this.button.disabled = true;
        return;
      }

      const fitmentKey = this.getFitmentKey();

      if (fitmentKey) {
        this.form.setAttribute("action", this.constructFitmentURL());
        this.button.disabled = false;
      }
    }

    onSubModelChange() {
      const year = this.fieldMap.year?.value;
      const make = this.fieldMap.make?.value;
      const model = this.fieldMap.model?.value;
      const subModel = this.fieldMap.subModel?.value;

      this.clearAndDisableAfter("subModel");
      this.button.disabled = true;

      if (
        !year ||
        !make ||
        !model ||
        !subModel ||
        !this.fitmentData?.[year]?.[make]?.[model]?.[subModel]
      ) {
        return;
      }

      const enginesRaw = this.fitmentData[year][make][model][subModel];

      const engines = enginesRaw
        .map((engine) => String(engine || "").trim())
        .filter((engine) => engine !== "");

      if (engines.length > 0) {
        this.populateSelect(this.fieldMap.engine, engines);
        this.button.disabled = true;
        return;
      }

      const fitmentKey = this.getFitmentKey();

      if (fitmentKey) {
        this.form.setAttribute("action", this.constructFitmentURL());
        this.button.disabled = false;
      }
    }

    onEngineChange() {
      const fitmentKey = this.getFitmentKey();

      this.button.disabled = !fitmentKey;

      if (fitmentKey) {
        this.form.setAttribute("action", this.constructFitmentURL());
      }
    }

    clearAndDisableAfter(fieldName) {
      const order = ["year", "make", "model", "subModel", "engine"];
      const startIndex = order.indexOf(fieldName);

      order.slice(startIndex + 1).forEach((key) => {
        this.clearSelect(this.fieldMap[key]);
        this.disableSelect(this.fieldMap[key]);
      });
    }

    getFitmentKey() {
      const year = this.fieldMap.year?.value || "";
      const make = this.fieldMap.make?.value || "";
      const model = this.fieldMap.model?.value || "";
      const subModel = this.fieldMap.subModel?.value || "";
      const engine = this.fieldMap.engine?.value || "";

      if (!year || !make || !model) {
        return "";
      }

      return `${year}|${make}|${model}|${subModel}|${engine}`;
    }

    constructFitmentURL() {
      const fitmentKey = this.getFitmentKey();

      if (!fitmentKey) return "";

      const collectionUrl = this.getCollectionUrl();
      const url = new URL(collectionUrl, window.requestShopUrl || window.location.origin);

      url.searchParams.set("filter.p.m.custom.fitment_key", fitmentKey);

      return url.toString();
    }

    toggleLoading(force) {
      if (typeof force === "boolean") {
        this.classList.toggle("is-loading", force);
        this.button?.classList.toggle("loading", force);
        return;
      }

      this.classList.toggle("is-loading");
      this.button?.classList.toggle("loading");
    }
  }

  customElements.define("collections-filtering", CollectionsFiltering);
}